/**
 * @file Secrets hooks — list (metadata only) + rotate.
 * @module @labcraft/admin/ui/hooks/use-secrets
 *
 * `useSecretsList` reads `GET /api/secrets` — per-secret declarations
 * + rotation metadata; values never cross the wire. `useRotateSecretMutation`
 * sends a new value via `POST /api/secrets/rotate`; on success it
 * invalidates the whole `['admin','secrets']` prefix so the panel
 * refreshes the version + timestamp on the row.
 *
 * Convention: hooks live in this directory and never call `useQuery`
 * / `useMutation` outside it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { RotateSecretRequest } from '@labcraft/openbao/shared'
import type { Admin } from '../../client/index.ts'
import type {
  RotateSecretResponse,
  SecretsListResponse,
} from '../../shared/secrets_routes.ts'
import { qkSecretsList } from './query_keys.ts'

/** Per-secret declarations + rotation metadata. Values not returned. */
export function useSecretsList() {
  const client = useClient<Admin>()
  return useQuery<SecretsListResponse>({
    queryKey: qkSecretsList,
    queryFn: () => client.secrets.list(),
  })
}

/**
 * Rotate one secret. On success, invalidates the whole
 * `['admin','secrets']` prefix so the panel re-renders with the new
 * version + timestamp.
 */
export function useRotateSecretMutation() {
  const client = useClient<Admin>()
  const queryClient = useQueryClient()
  return useMutation<RotateSecretResponse, Error, RotateSecretRequest>({
    mutationFn: body => client.secrets.rotate(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'secrets'] })
    },
  })
}
